import React from 'react';
import { motion } from 'framer-motion';

const BudgetShoppingLandingPage = ({ goToInput, theme }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Shopping on a Budget: Track Spending & Eliminate Purchase Regret
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Stay Within Budget, Shop Without Guilt</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Track your purchases, set spending limits, and get personalized advice to make the most of your budget.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Purchase
            </motion.button>
          </div>
          <div className="text-8xl">💸</div>
        </div>
      </div>
      
      {/* Budget shopping challenges section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Budget Shopping Challenges</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💳</span>
              <div>
                <h3 className="font-bold dark:text-white">Impulse Spending</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Unplanned purchases that break your budget and cause financial stress.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">👀</span>
              <div>
                <h3 className="font-bold dark:text-white">FOMO Purchases</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Buying due to fear of missing out rather than actual need or financial plan.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🛍️</span>
              <div>
                <h3 className="font-bold dark:text-white">Sale Traps</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Spending on "deals" that weren't in your budget to begin with.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">📊</span>
              <div>
                <h3 className="font-bold dark:text-white">Budget Tracking</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Difficulty maintaining awareness of where your money is going.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How Guilt Gauge helps */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Our Budget Tools Help You Shop Smarter</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Track Every Purchase
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Log your shopping expenses, complete with emotional context and practical usage metrics.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Set Personalized Budgets
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Create custom spending limits for different categories that align with your financial goals.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Visualize Your Spending
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              See clear charts and breakdowns of where your money goes to identify budget leaks.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Get Budget-Conscious Advice
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Receive personalized recommendations to maximize value while minimizing spending.
            </p>
          </div>
        </div>
      </section>
      
      {/* Budget Shopper Types Section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Discover Your Budget Shopper Type</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🔍</span>
              <h3 className="font-bold mt-2 dark:text-white">Value Seeker</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You research extensively before buying to ensure you're getting the best possible deal within your budget.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">✂️</span>
              <h3 className="font-bold mt-2 dark:text-white">Coupon Collector</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You never pay full price and have a strategic approach to stacking discounts for maximum savings.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🛒</span>
              <h3 className="font-bold mt-2 dark:text-white">Bulk Buyer</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You focus on economies of scale, saving by purchasing in larger quantities when your budget allows.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">⏳</span>
              <h3 className="font-bold mt-2 dark:text-white">Patient Planner</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You save by waiting for the right moment to buy, planning purchases around your budget cycles.
            </p>
          </div>
        </div>
      </section>
      
      {/* Budget Savings Calculator Teaser */}
      <section className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
        <h2 className="text-xl font-bold mb-4 dark:text-white">Budget Savings Potential</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">15%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average shopping savings with budget tracking</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">$1,200</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">potential annual savings for the average shopper</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">42%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">reduction in impulse purchases with active tracking</p>
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Budget Tracking Changed Their Shopping</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I used to go over budget every month without realizing where the money was going. After tracking my purchases for just two weeks, I identified $300 in unnecessary spending I could eliminate."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Michael R.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Saved $2,800 in 10 months</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "As a student on a tight budget, I thought I was being careful with money. The purchase tracking showed me I was spending too much on small impulse buys that added up. Now I stick to my planned purchases."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Sara T.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Cut monthly spending by 22%</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Budget Shopping Tips Section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">7 Smart Budget Shopping Strategies</h2>
        <div className="space-y-3">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2 mt-0.5">1</div>
              <div>
                <h3 className="font-bold dark:text-white">The 24-Hour Rule</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Wait 24 hours before making any unplanned purchase over $50 to avoid impulse buying.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2 mt-0.5">2</div>
              <div>
                <h3 className="font-bold dark:text-white">Cash Envelope System</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Allocate cash to different spending categories in physical or digital "envelopes" to avoid overspending.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2 mt-0.5">3</div>
              <div>
                <h3 className="font-bold dark:text-white">Category Caps</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Set specific spending limits for each category (clothing, entertainment, groceries) and track against them.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2 mt-0.5">4</div>
              <div>
                <h3 className="font-bold dark:text-white">One-In-One-Out Rule</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  For every new item you buy, remove one similar item from your home to control accumulation.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2 mt-0.5">5</div>
              <div>
                <h3 className="font-bold dark:text-white">Price Per Use Calculation</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Divide the cost of an item by how many times you'll realistically use it to determine its true value.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2 mt-0.5">6</div>
              <div>
                <h3 className="font-bold dark:text-white">No-Spend Challenges</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Designate certain days or weeks as "no-spend" periods where you only purchase absolute necessities.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <div className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2 mt-0.5">7</div>
              <div>
                <h3 className="font-bold dark:text-white">Guilt-Free Fun Money</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Set aside a small portion of your budget specifically for impulse or fun purchases with no guilt attached.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About Budget Shopping</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How much should I budget for shopping each month?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Financial experts typically recommend allocating 5-10% of your take-home pay for shopping and discretionary spending. Our tool helps you personalize this based on your specific financial situation and goals.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I stick to my shopping budget without feeling deprived?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Include a "fun money" category in your budget for guilt-free spending, focus on quality over quantity, and track your emotional response to purchases. Our tool helps identify which purchases genuinely increase your satisfaction.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">What's the most effective way to track my shopping expenses?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              The most effective tracking combines regular logging of purchases with emotional context. Our tool goes beyond simple expense tracking by analyzing your spending patterns, triggers, and the value you get from each purchase.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How do I handle unexpected shopping needs in my budget?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Create a specific "buffer" category in your budget for unexpected needs. Our tool helps you differentiate between genuine needs and impulse wants, while providing strategies for managing surprise expenses.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Start Shopping Smarter Today</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Track your purchases, set your budget goals, and transform your relationship with shopping.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Start Budget Tracking Now
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Budget Shopping</h2>
          <p className="mb-3">
            Budget shopping doesn't mean sacrificing quality or enjoyment. It's about making intentional choices that align with your financial goals while maximizing the value of every dollar spent.
          </p>
          <p className="mb-3">
            Our Budget Shopping Tool helps you understand your spending patterns, identify budget leaks, and develop strategies that work with your unique financial situation. By tracking both the financial and emotional aspects of your purchases, you'll develop healthier spending habits.
          </p>
          <p>
            Whether you're saving for a specific goal, working to reduce debt, or simply wanting to be more mindful with your money, our free tool helps you shop smarter and make purchases you'll feel good about both now and in the future.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default BudgetShoppingLandingPage;
